import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Sun, Leaf, Snowflake, Sprout, AlertTriangle, Loader2, Info
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  sun: Sun,
  leaf: Leaf,
  snowflake: Snowflake,
  sprout: Sprout,
};

/**
 * SeasonalFocusCard
 * -----------------
 * Dashboard widget. Shows the current season, intensity/volume multiplier,
 * priority focus areas, and a phase alignment warning if misaligned.
 */
const SeasonalFocusCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/season/current`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-[#0A0A0A] border border-white/[0.06] p-5 flex items-center gap-3" data-testid="seasonal-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-white/40" />
        <span className="text-xs font-mono text-white/40">LOADING SEASONAL FOCUS...</span>
      </div>
    );
  }

  if (!data?.season) return null;

  const s = data.season;
  const Icon = ICON_MAP[s.icon] || Sun;
  const volPct = Math.round((s.volume_multiplier - 1) * 100);
  const intensityPct = Math.round((s.intensity_multiplier - 1) * 100);

  return (
    <div
      className="border overflow-hidden"
      style={{ borderColor: `${s.color}40`, background: `linear-gradient(135deg, ${s.color}15 0%, #0A0A0A 70%)` }}
      data-testid="seasonal-focus-card"
    >
      {/* Top strip */}
      <div className="px-5 py-2 flex items-center justify-between" style={{ background: "rgba(0,0,0,0.4)" }}>
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color: s.color }} />
          <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: s.color }}>
            SEASONAL FOCUS
          </p>
        </div>
        {s.is_override && (
          <span className="text-[9px] font-mono uppercase tracking-wider text-white/40">MANUAL</span>
        )}
      </div>

      <div className="p-5">
        {/* Label + headline */}
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">{s.label}</p>
        <h3 className="text-2xl md:text-3xl font-heading uppercase text-white tracking-tight mt-0.5" data-testid="season-headline">
          {s.headline}
        </h3>

        {/* Focus narrative */}
        <p className="text-sm text-white/70 mt-3 leading-relaxed">{s.focus}</p>

        {/* Multipliers */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <div className="bg-black/40 border border-white/[0.04] p-3">
            <p className="text-[9px] font-mono uppercase tracking-wider text-white/40 mb-1">Volume</p>
            <p className="text-xl font-heading" style={{ color: volPct >= 0 ? s.color : "#60A5FA" }}>
              {volPct >= 0 ? "+" : ""}{volPct}%
            </p>
            <p className="text-[9px] font-mono text-white/30 mt-0.5">vs. baseline</p>
          </div>
          <div className="bg-black/40 border border-white/[0.04] p-3">
            <p className="text-[9px] font-mono uppercase tracking-wider text-white/40 mb-1">Intensity</p>
            <p className="text-xl font-heading" style={{ color: intensityPct >= 0 ? s.color : "#60A5FA" }}>
              {intensityPct >= 0 ? "+" : ""}{intensityPct}%
            </p>
            <p className="text-[9px] font-mono text-white/30 mt-0.5">vs. baseline</p>
          </div>
        </div>

        {/* Priority areas */}
        <div className="mt-4">
          <p className="text-[9px] font-mono uppercase tracking-wider text-white/40 mb-2">Priority this season</p>
          <div className="flex flex-wrap gap-1.5">
            {s.priority_areas.map((p, i) => (
              <span
                key={i}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1"
                style={{ background: `${s.color}15`, color: s.color, border: `1px solid ${s.color}30` }}
              >
                {p}
              </span>
            ))}
          </div>
        </div>

        {/* Warning (season-specific) */}
        {s.warning && (
          <div className="mt-4 p-3 bg-amber-500/5 border border-amber-500/20 flex items-start gap-2" data-testid="season-warning">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200/80 leading-relaxed">{s.warning}</p>
          </div>
        )}

        {/* Phase alignment nudge */}
        {data.phase_alignment_warning && (
          <div className="mt-3 p-3 bg-sky-500/5 border border-sky-500/20 flex items-start gap-2" data-testid="phase-alignment-warning">
            <Info className="w-3.5 h-3.5 text-sky-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-sky-200/80 leading-relaxed">{data.phase_alignment_warning}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeasonalFocusCard;
